import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  CoreService,
  Param,
  PlanningService,
  ProleisObject,
  Task,
  TimetrackingBody,
  TimetrackingsService,
  Types,
  UserService,
} from 'proleis-rest-client';
import {PlanningActions} from './planning.index';
import {concatMap, exhaustMap, map, mergeMap, switchMap, tap,} from 'rxjs/operators';
import {CurrentViewService} from 'proleis-web-app';
import {forkJoin} from 'rxjs';
import {Location} from '@angular/common';
import {
  GUID_DIR_WERTUNGSARTEN,
  GUID_OBJECT_PLAN_ROOT,
  GUID_PATH_PLAN,
  GUID_PATH_PREDECESSORS,
  GUID_PATH_SUCCESSORS,
  GUID_PATH_VIEW,
} from '../../../p4m/guids';
import {NotificationService} from '../../../p4m/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {PpsService} from "../../planning/components/pps/board/pps.service";

const aliasFieldsTask = 'TYP_GUID=TYP_ID.GUID;INFOTEXT=OBJECT_ID.INFOTEXT;' +
  'PREDECESSORS=$JOIN(CHILDREN(PATH=' +
  GUID_PATH_PREDECESSORS +
  '),",");SUCCESSORS=$JOIN(CHILDREN(PATH=' +
  GUID_PATH_SUCCESSORS +
  '),",");RESNAME=RES_ID.TEXT';

@Injectable()
export class PlanningEffects {
  constructor(
    private actions: Actions,
    private coreService: CoreService,
    private userService: UserService,
    private currentViewService: CurrentViewService,
    private planningService: PlanningService,
    private notificationService: NotificationService,
    private location: Location,
    private translateService: TranslateService,
    private ppsService: PpsService,
    private timetrackingService: TimetrackingsService
  ) {
  }

  loadResources$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.loadPpsResources),
      exhaustMap((action) => {
        return this.userService
          .getUserResources()
          .pipe(
            map((resources) =>
              PlanningActions.onPpsResourcesLoaded({resources}),
            ),
          );
      }),
    );
  });

  onResourcesLoaded$ = createEffect(
    () => {
      return this.actions.pipe(
        ofType(PlanningActions.onPpsResourcesLoaded),
        tap((action) => this.currentViewService.setLoading(false)),
      );
    },
    {
      dispatch: false,
    },
  );

  loadPpsResourceInfo$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.loadPpsResourceInfoState),
      concatMap((action) => {
        this.currentViewService.setLoading(true);
        return this.coreService.getObjectByGuid<ProleisObject>(GUID_PATH_PLAN).pipe(
          exhaustMap((planPath) => {
            const aliasFields: string[] = [aliasFieldsTask,
              this.ppsService.getDynamicAliasFields(),
              this.ppsService.getHideIfAlias()];
            return this.coreService
              .getChildren<
                Task[]
              >(action.resId, planPath.OBJECT_ID, aliasFields.join(';'), null)
              .pipe(
                map((tasks) =>
                  PlanningActions.onPpsResourceInfoStateLoaded({
                    resourceInfo: {
                      resId: action.resId,
                      tasks,
                    },
                  }),
                ),
              );
          }),
        );
      }),
    );
  });

  onPpsResourceInfoLoaded$ = createEffect(
    () => {
      return this.actions.pipe(
        ofType(PlanningActions.onPpsResourceInfoStateLoaded),
      );
    },
    {
      dispatch: false,
    },
  );

  setTaskInProgress$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.setTaskInProgress),
      exhaustMap((action) => {
        this.currentViewService.setLoading(true);
        return forkJoin({
          task: this.coreService.getObjectById<Task>(action.taskId),
          result: this.coreService.executeAction(
            action.taskId,
            '§69697338-28ff-11b2-8057-2735d8eeb821',
            null,
          ),
        }).pipe(
          map((task, result) =>
            PlanningActions.onSetTaskInProgress({resId: action.resId}),
          ),
        );
      }),
    );
  });

  onSetTaskInProgess = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.onSetTaskInProgress),
      map((action) =>
        PlanningActions.loadPpsResourceInfoState({
          resId: action.resId,
        }),
      ),
    );
  });

  setTaskComplete$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.setTaskComplete),
      exhaustMap((action) => {
        this.currentViewService.setLoading(true);
        return this.planningService
          .setTaskComplete(action.taskId)
          .pipe(
            map((result) =>
              PlanningActions.onSetTaskComplete({resId: action.resId}),
            ),
          );
      }),
    );
  });

  onSetTaskComplete$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.onSetTaskComplete),
      map((action) =>
        PlanningActions.loadPpsResourceInfoState({resId: action.resId}),
      ),
    );
  });

  setTaskInterrupted$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.setTaskInterrupted),
      exhaustMap((action) => {
        this.currentViewService.setLoading(true);
        return this.coreService
          .executeAction(action.taskId, '§0bf69513-2ad7-11b2-8010-032c4d2fc27a')
          .pipe(
            map((result) =>
              PlanningActions.onSetTaskComplete({
                resId: action.resId,
              }),
            ),
          );
      }),
    );
  });

  onSetTaskInterrupted$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.onSetTaskInterrupted),
      map((action) =>
        PlanningActions.loadPpsResourceInfoState({
          resId: action.resId,
        }),
      ),
    );
  });

  setFilter$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.setPpsFilter),
      exhaustMap((action) => {
        const res = action.resId;
        const params: Param[] = [];
        params.push({
          name: 'FILTER_GEQ_STARTPLAN',
          value: action.start ? action.start : '',
          type: Types.TEXT,
        });
        params.push({
          name: 'FILTER_LEQ_ENDEPLAN',
          value: action.end ? action.end : '',
          type: Types.TEXT,
        });
        params.push({
          name: 'FILTER_LEQ_PLANVORLAUF',
          value: action.lead,
          type: Types.DOUBLE,
        });
        params.push({
          name: 'FILTER_EQ_ISTBEARBEITET',
          value: action.includeFinished,
          type: Types.BOOL,
        });
        params.push({
          name: 'FILTER_GEQ_FREIGABE_STATUS',
          value: Number(action.onlyReleased),
          type: Types.DOUBLE,
        });
        return this.coreService
          .getObjectByGuid<ProleisObject>(GUID_OBJECT_PLAN_ROOT)
          .pipe(
            exhaustMap((planRoot) => {
              return this.coreService
                .updateObject(planRoot.OBJECT_ID, params)
                .pipe(
                  switchMap((result) => [
                    PlanningActions.onPpsFilterSet({resId: res}),
                  ]),
                );
            }),
          );
      }),
    );
  });

  onFilterSet$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.onPpsFilterSet),
      map((action) =>
        PlanningActions.loadPpsResourceInfoState({resId: action.resId}),
      ),
    );
  });

  loadFilter$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.loadPpsFilter),
      exhaustMap((action) => {
        const aliasFields =
          'START=FILTER_GEQ_STARTPLAN;END=FILTER_LEQ_ENDEPLAN;' +
          'LEAD=FILTER_LEQ_PLANVORLAUF;INCLUDE_FINISHED=FILTER_EQ_ISTBEARBEITET;ONLY_RELEASED=FILTER_GEQ_FREIGABE_STATUS;' +
          'RANGE_START=OBJECT_ID.CALC_RANGE_STARTDATE;RANGE_END=OBJECT_ID.CALC_RANGE_ENDDATE';
        this.currentViewService.setLoading(true);
        return this.coreService
          .getObjectByGuid<ProleisObject>(GUID_OBJECT_PLAN_ROOT, aliasFields)
          .pipe(
            map((planRoot) =>
              PlanningActions.onPpsFilterLoaded({
                start: planRoot.FILTER_GEQ_STARTPLAN
                  ? new Date(planRoot.FILTER_GEQ_STARTPLAN)
                  : null,
                end: planRoot.FILTER_LEQ_ENDEPLAN
                  ? new Date(planRoot.FILTER_LEQ_ENDEPLAN)
                  : null,
                lead: planRoot.LEAD ? planRoot.LEAD : 0,
                onlyReleased: planRoot.ONLY_RELEASED
                  ? planRoot.ONLY_RELEASED
                  : 0,
                includeFinished: planRoot.INCLUDE_FINISHED,
                rangeEnd: planRoot.RANGE_END,
                rangeStart: planRoot.RANGE_START,
              }),
            ),
          );
      }),
    );
  });

  onFilterLoaded$ = createEffect(
    () => {
      return this.actions.pipe(
        ofType(PlanningActions.onPpsFilterLoaded),
        tap((action) => this.currentViewService.setLoading(false)),
      );
    },
    {dispatch: false},
  );

  createTimeTracking$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.createTimeTracking),
      mergeMap((action) => {
        return this.userService
          .createTimeTracking(action.body, action.startObjectId)
          .pipe(
            map((result) => PlanningActions.onTimeTrackingCreated({result})),
          );
      }),
    );
  });

  onTimeTrackingCreated$ = createEffect(
    () => {
      return this.actions.pipe(
        ofType(PlanningActions.onTimeTrackingCreated),
        tap((action) => this.currentViewService.setLoading(false)),
        tap((action) =>
          this.notificationService.showNotification(
            this.translateService.instant('planning.general.trackingCreated'),
            'success',
          ),
        ),
        tap((action) => this.location.back()),
      );
    },
    {dispatch: false},
  );

  loadValuationTypes$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.loadEvaluationTypes),
      exhaustMap((action) => {
        return forkJoin({
          valDir: this.coreService.getObjectByGuid<ProleisObject>(
            GUID_DIR_WERTUNGSARTEN,
          ),
          viewPath: this.coreService.getObjectByGuid<ProleisObject>(GUID_PATH_VIEW),
        }).pipe(
          exhaustMap((value) => {
            return this.coreService
              .getChildren<
                ProleisObject[]
              >(value.valDir.OBJECT_ID, value.viewPath.OBJECT_ID)
              .pipe(
                map((valTypes) =>
                  PlanningActions.onEvaluationTypesLoaded({
                    evaluationTypes: valTypes,
                  }),
                ),
              );
          }),
        );
      }),
    );
  });

  loadUserBookings$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.loadUserBookings),
      exhaustMap((action) => {
        this.currentViewService.setLoading(true);
        return this.timetrackingService
          .getTimetrackings(
            action.start,
            action.end,
            action.position,
            action.aliasFields,
            action.fields,
            action.userId
          )
          .pipe(
            map((bookings) =>
              PlanningActions.onUserBookingsLoaded({bookings}),
            ),
          );
      }),
    );
  });

  onUserBookingsLoaded$ = createEffect(
    () => {
      return this.actions.pipe(
        ofType(PlanningActions.onUserBookingsLoaded),
        tap((action) => this.currentViewService.setLoading(false)),
      );
    },
    {dispatch: false},
  );

  loadBookingMetadata$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.loadBookingMetaData),
      exhaustMap((action) => {
        return this.coreService.getObjectById<ProleisObject>(action.objectId).pipe(
          exhaustMap((task) => {
            return forkJoin({
              booking: this.userService.getUserTimeTracking(),
              task: this.coreService.getObjectById<ProleisObject>(task.OBJECT_ID),
              evaluationType: this.coreService.getObjectById<ProleisObject>(
                task.TAETIGKEIT_ID,
              ),
              costCentre: this.coreService.getObjectById<ProleisObject>(
                task.KOSTENSTELLE_ID,
              ),
              resource: this.coreService.getObjectById<ProleisObject>(task.AFO_RES_ID),
              pse: this.coreService.getObjectById<ProleisObject>(task.WERKZEUG),
              position: this.coreService.getObjectById<ProleisObject>(task.POSITION_ID),
            }).pipe(
              map((result) => {
                return PlanningActions.onBookingMetaDataLoaded({
                  metadata: {
                    booking: result.booking[0],
                    evaluationType: result.evaluationType,
                    resource: result.resource,
                    task: result.task,
                    position: result.position,
                    pse: result.pse,
                  },
                });
              }),
            );
          }),
        );
      }),
    );
  });

  startTimeTracking$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.startTimeTracking),
      exhaustMap((action) => {
        return this.userService
          .startTimeTracking(action.taskId)
          .pipe(
            map((result) =>
              PlanningActions.onTimeTrackingStarted({taskId: action.taskId}),
            ),
          );
      }),
    );
  });

  onTimeTrackingStarted$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.onTimeTrackingStarted),
      tap((result) =>
        this.notificationService.showNotification(
          this.translateService.instant('planning.general.trackingStarted'),
          'success',
        ),
      ),
      map((action) => PlanningActions.refreshTask({taskId: action.taskId})),
    );
  });

  stopTimeTracking$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.stopTimeTracking),
      exhaustMap((action) => {
        const body: TimetrackingBody = {
          taskId: Number(action.taskId)
        }
        return this.timetrackingService.stopTracking(body).pipe(
          map((result) =>
            PlanningActions.onTimeTrackingStopped({
              taskId: action.taskId,
            }),
          ),
        );
      }),
    );
  });

  onTimeTrackingStopped$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.onTimeTrackingStopped),
      tap((result) =>
        this.notificationService.showNotification(
          this.translateService.instant('planning.general.trackingStopped'),
          'success',
        ),
      ),
      map((action) => PlanningActions.refreshTask({taskId: action.taskId})),
    );
  });

  loadActiveBooking$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.loadActiveBooking),
      exhaustMap((action) => {
        return this.userService
          .getCurrentBookings()
          .pipe(
            map((result) =>
              PlanningActions.onActiveBookingLoaded({activeBookings: result}),
            ),
          );
      }),
    );
  });

  refreshTask$ = createEffect(() => {
    return this.actions.pipe(
      ofType(PlanningActions.refreshTask),
      exhaustMap((action) => {
        return this.coreService.getObjectById<Task>(action.taskId, aliasFieldsTask).pipe(
          map((result) => {
            return PlanningActions.onTaskRefreshed({task: result});
          }),
        );
      }),
    );
  });
}
